import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users2, Briefcase, HandCoins, Trophy, TrendingUp, Timer, Share2, Wallet } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="card-surface p-6">
      <div className="flex items-center gap-3 text-[var(--ink-soft)]">
        <div className={`w-9 h-9 rounded-xl grid place-items-center ${accent}`}><Icon className="w-4 h-4"/></div>
        <div className="eyebrow !text-[var(--ink-soft)]">{label}</div>
      </div>
      <div className="font-heading text-4xl mt-3 text-[var(--ink)]">{value}</div>
      {sub && <div className="text-xs text-[var(--ink-soft)] mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminHome() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats/admin").then(({ data }) => setStats(data)); }, []);
  if (!stats) return <div className="text-[var(--ink-soft)]">A carregar…</div>;

  const currency = (n) => `${Number(n || 0).toFixed(2)} €`;

  return (
    <div className="space-y-10">
      <div>
        <div className="eyebrow">Administração</div>
        <h1 className="font-heading text-5xl mt-2">Painel global</h1>
        <p className="text-[var(--ink-soft)] mt-2">Eficácia do programa CARING · Referências.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
        <Stat icon={Users2} label="Referenciadores" value={stats.total_referrers} accent="bg-[var(--muted)] text-[var(--forest)]"/>
        <Stat icon={Briefcase} label="Vagas abertas" value={stats.total_vacancies_open} accent="bg-[rgba(59,113,151,0.12)] text-[var(--info)]"/>
        <Stat icon={TrendingUp} label="Candidatos" value={stats.total_candidates} accent="bg-[rgba(217,108,74,0.12)] text-[var(--terracotta)]"/>
        <Stat icon={Trophy} label="Contratações" value={stats.total_hires} accent="bg-[rgba(45,90,39,0.12)] text-[var(--success)]"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat icon={Wallet} label="Total pago" value={currency(stats.total_paid)} accent="bg-[rgba(45,90,39,0.12)] text-[var(--success)]"/>
        <Stat icon={HandCoins} label="Pendente/agendado" value={currency(stats.total_pending)} accent="bg-[rgba(217,108,74,0.12)] text-[var(--terracotta)]"/>
        <Stat icon={Timer} label="Tempo médio p/ contratação" value={`${Number(stats.avg_days_to_hire || 0).toFixed(1)} dias`} accent="bg-[var(--muted)] text-[var(--forest)]"/>
      </div>

      <div className="card-surface p-6">
        <div className="eyebrow">Custo por contratação</div>
        <div className="font-heading text-4xl mt-2 text-[var(--forest)]">{currency(stats.cost_per_hire)}</div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface p-6">
          <div className="flex items-center gap-2 eyebrow"><Trophy className="w-4 h-4"/> Top referenciadores</div>
          {stats.top_referrers.length === 0 ? (
            <p className="text-[var(--ink-soft)] mt-3 text-sm">Ainda sem dados.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {stats.top_referrers.map((r, i) => (
                <li key={r.id} className="flex items-center justify-between p-3 rounded-xl bg-[var(--muted)]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[var(--forest)] text-white grid place-items-center text-xs font-semibold">{i+1}</div>
                    <div>
                      <div className="font-semibold text-sm">{r.name}</div>
                      <div className="text-xs text-[var(--ink-soft)]">{r.code}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-lg">{r.recommendations} rec.</div>
                    <div className="text-xs text-[var(--success)]">{currency(r.total_earned)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-surface p-6">
          <div className="flex items-center gap-2 eyebrow"><Share2 className="w-4 h-4"/> Vagas mais partilhadas</div>
          {stats.top_shared_vacancies.length === 0 ? (
            <p className="text-[var(--ink-soft)] mt-3 text-sm">Ainda sem dados.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {stats.top_shared_vacancies.map((v, i) => (
                <li key={v.id} className="flex items-center justify-between p-3 rounded-xl bg-[var(--muted)]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[var(--terracotta)] text-white grid place-items-center text-xs font-semibold">{i+1}</div>
                    <div className="font-semibold text-sm">{v.title}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-lg">{v.shares} partilhas</div>
                    <div className="text-xs text-[var(--ink-soft)]">{v.views} views</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="eyebrow">Origem das recomendações</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.entries(stats.sources || {}).length === 0 ? (
            <p className="text-[var(--ink-soft)] text-sm">Sem dados ainda.</p>
          ) : (
            Object.entries(stats.sources).map(([k, v]) => (
              <span key={k} className="chip chip-neutral">{k}: {v}</span>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
